/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */

import { Logger } from "../../../clients/logger";
import { BootTaskCallbackInterface } from "../types";
import { StandardCallback } from "../../../types";
import { RouterClient } from "../../../clients/routerClient";
import { ConfigClient } from "../../../clients/configClient";
import { WorkspaceClient } from "../../../clients/workspaceClient";
import { DialogManagerClient } from "../../../clients/dialogManagerClient";

const SHUTDOWN_TIMEOUT = 60000;

const CONFIG_LOCATION_SHUTDOWN = "finsemble.scheduledShutdown";
const CONFIG_LOCATION_DISCARD = "finsemble.scheduledCloseDiscardUnsavedChanges";

/**
 * Calculates the number of milliseconds given a WeeklyRecurringTime config
 **/
export const calculateTimeout = (config): number => {
	const daysUntilShutdown = (restartDay, today) => {
		const shutdownIsToday = restartDay - today === 0;
		const shutdownIsNextWeek = restartDay - today < 0;

		if (shutdownIsToday) {
			return 0;
		} else if (shutdownIsNextWeek) {
			// 6 is how many full days until the same day next week
			return restartDay - today + 6;
		} else {
			// shutdown day is coming up soon this week
			return restartDay - today;
		}
	};

	const now = new Date();

	const shutdownTime = new Date();
	shutdownTime.setSeconds(0);

	let repeat = 1;

	if (config.dayOfWeek != null) {
		// weekly shutdown - using dayOfWeek to set the date for shutdown
		shutdownTime.setDate(now.getDate() + daysUntilShutdown(config.dayOfWeek, now.getDay()));
		repeat = 7;
	}

	const { hour } = config;

	shutdownTime.setHours(hour);

	shutdownTime.setMinutes(config.minute);
	if (shutdownTime.getTime() - now.getTime() <= 0) {
		// If the time has passed then set the day to next week (same day)
		shutdownTime.setDate(shutdownTime.getDate() + repeat);
	}
	Logger.system.log("APPLICATION LIFECYCLE:SCHEDULED RESTART/SHUTDOWN at: ", shutdownTime);
	return shutdownTime.getTime() - now.getTime();
};

/**
 * A boot task that watches the shutdown config, and then schedules and shuts down Finsemble at the appropriate time
 *
 * @private
 */
export class CheckForScheduledShutdownTask {
	shutdownTimer: number | null = null;

	discardUnsavedChanges: boolean = false;

	public start(doneCallback: BootTaskCallbackInterface) {
		Logger.system.debug("checkForScheduledShutdownTask start");
		this.bindFunctions();
		this.checkConfig();

		doneCallback("checkForScheduledShutdownTask", "bootTasks", "completed");
	}

	private bindFunctions() {
		this.processShutdownConfig = this.processShutdownConfig.bind(this);
		this.processDiscardConfig = this.processDiscardConfig.bind(this);
	}

	private processShutdownConfig: StandardCallback = (err, response) => {
		let config: any;

		/**
		 * Response could be either response or response.value because the return type is different
		 * for `ConfigClient.getValue` and `ConfigClient.addListener` but the business logic is identical
		 */
		if (response?.value) {
			config = response.value;
			Logger.system.log("APPLICATION LIFECYCLE:SCHEDULED SHUTDOWN - CONFIG CHANGED. NEW CONFIG:", config);
		} else if (response) {
			config = response;
			Logger.system.log("APPLICATION LIFECYCLE:SCHEDULED SHUTDOWN - config:", config);
		} else if (!err) {
			Logger.system.log("APPLICATION LIFECYCLE:SCHEDULED SHUTDOWN - not configured.");
		}

		if (err) {
			Logger.error(err);
		} else {
			this.scheduleShutdown(config);
		}
	};

	private processDiscardConfig: StandardCallback = (err, response) => {
		if (err) {
			Logger.error(err);
		} else {
			let config = false;
			/**
			 * Response could be either response or response.value because the return type is different
			 * for `ConfigClient.getValue` and `ConfigClient.addListener` but the business logic is identical
			 */
			if (response?.value) {
				config = response.value;
			} else if (response) {
				config = response;
			}
			this.discardUnsavedChanges = config;
			Logger.system.log(
				"APPLICATION LIFECYCLE:SCHEDULED SHUTDOWN - DISCARD UNSAVED CHANGES CHANGED. NEW CONFIG:",
				this.discardUnsavedChanges
			);
		}
	};

	public isValidConfig(config: any): boolean {
		if (!config) {
			return false;
		}

		if (config.hour == null || config.hour > 23 || config.hour < 0) {
			return false;
		}

		if (config.minute == null || config.minute > 59 || config.minute < 0) {
			return false;
		}

		return !(config.dayOfWeek && (config.dayOfWeek < 0 || config.dayOfWeek > 6));
	}

	private scheduleShutdown(config: any) {
		if (!this.isValidConfig(config)) {
			return;
		}

		if (this.shutdownTimer) {
			// clear the timer just in case there is an existing one set up
			clearTimeout(this.shutdownTimer);
		}

		const shutdownTimeout: number = calculateTimeout(config);
		// countdown timer until the shutdown
		this.shutdownTimer = setTimeout(this.shutdown, shutdownTimeout);
	}

	private shutdown: TimerHandler = async () => {
		DialogManagerClient.open(
			"yesNo",
			{
				monitor: "primary",
				title: "Scheduled Shutdown",
				question: `The application will shutdown in one minute. ${
					this.discardUnsavedChanges
						? "Unsaved changes in your workspace will be discarded"
						: "Your workspace will be saved"
				}.`,
				showTimer: true,
				timerDuration: SHUTDOWN_TIMEOUT,
				showNegativeButton: false,
				affirmativeResponseLabel: "Shutdown Now",
			},
			async (error, response) => {
				if (error) throw new Error(error.toString());
				Logger.log("APPLICATION LIFECYCLE:SCHEDULED SHUTDOWN - done with shutdown dialog, response", response);
				try {
					if (response.choice === "cancel") {
						Logger.system.warn("APPLICATION LIFECYCLE:SCHEDULED SHUTDOWN - Scheduled shutdown cancelled by user");
						// User cancelled set up the timer again
						ConfigClient.getValue({ field: CONFIG_LOCATION_SHUTDOWN }, this.processShutdownConfig);
					} else {
						if (this.discardUnsavedChanges) {
							// don't save the workspace
							Logger.system.info("APPLICATION LIFECYCLE:SCHEDULED SHUTDOWN - Discarding unsaved workspace changes.");
						} else {
							// save the workspace
							Logger.system.info("APPLICATION LIFECYCLE:SCHEDULED SHUTDOWN - Saving workspace.");
							await WorkspaceClient.save();
						}

						Logger.system.info("APPLICATION LIFECYCLE:SCHEDULED SHUTDOWN - Workspace saved successfully.");
						RouterClient.transmit("Application.shutdown", {
							scheduledCloseDiscardUnsavedChanges: this.discardUnsavedChanges,
						});
					}
				} catch (e) {
					const errorMessage = `APPLICATION LIFECYCLE:SCHEDULED SHUTDOWN - Scheduled shutdown cancelled as we failed to save the workspace`;
					Logger.warn(errorMessage, e);
				}
			}
		);
	};

	private checkConfig() {
		ConfigClient.getValue({ field: CONFIG_LOCATION_SHUTDOWN }, this.processShutdownConfig);
		ConfigClient.addListener({ field: CONFIG_LOCATION_SHUTDOWN }, this.processShutdownConfig);

		ConfigClient.getValue({ field: CONFIG_LOCATION_DISCARD }, this.processDiscardConfig);
		ConfigClient.addListener({ field: CONFIG_LOCATION_DISCARD }, this.processDiscardConfig);
	}
}
